import { useState } from "react";
import { ChevronDown, ChevronRight, ThumbsUp, ThumbsDown, HelpCircle } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { toast } from "@/shared/hooks/use-toast";
import { postAuditFeedback } from "./api";
import { FindingEvidence } from "./FindingEvidence";
import type { AuditFeedbackVerdict } from "./types";
import {
  getRecommendationCostEstimate,
  type RawDeficiency,
  type RawRecommendation,
} from "@/features/properties/components/propertyOverview/deficiencyHelpers";

const usd = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

interface AuditFindingDetailsProps {
  deficiency: RawDeficiency;
  recommendations: RawRecommendation[];
}

export function AuditFindingDetails({ deficiency, recommendations }: AuditFindingDetailsProps) {
  const [traceOpen, setTraceOpen] = useState(true);
  const [submitting, setSubmitting] = useState<AuditFeedbackVerdict | null>(null);
  const [submitted, setSubmitted] = useState<AuditFeedbackVerdict | null>(null);
  const explanation = deficiency.explanation;

  const sendFeedback = async (verdict: AuditFeedbackVerdict) => {
    if (!deficiency.id) return;
    setSubmitting(verdict);
    try {
      await postAuditFeedback(deficiency.id, { verdict });
      setSubmitted(verdict);
      toast({ title: "Thanks — feedback recorded." });
    } catch (e) {
      toast({
        title: "Couldn't save feedback",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="space-y-3">
      <FindingEvidence deficiencyId={deficiency.id} />

      {explanation && (
        <div className="rounded-lg ring-1 ring-border/30 bg-muted/20">
          <button
            type="button"
            onClick={() => setTraceOpen((v) => !v)}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-foreground"
          >
            {traceOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            How we decided
          </button>
          {traceOpen && (
            <div className="space-y-2 px-3 pb-3 text-sm text-muted-foreground">
              <p className="text-foreground">{explanation.statement}</p>
              {explanation.inputs?.length > 0 && (
                <ul className="space-y-0.5">
                  {explanation.inputs.map((input, idx) => (
                    <li key={idx} className="flex justify-between gap-4">
                      <span>{input.label}</span>
                      <span className="font-mono text-foreground">{String(input.value)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {explanation.threshold && (
                <p>Threshold: <span className="text-foreground">{explanation.threshold}</span></p>
              )}
              {explanation.computation && (
                <p>Computation: <span className="font-mono text-foreground">{explanation.computation}</span></p>
              )}
              {explanation.outcome && (
                <p>Outcome: <span className="text-foreground">{explanation.outcome}</span></p>
              )}
              {explanation.rationale && <p className="italic">{explanation.rationale}</p>}
            </div>
          )}
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Recommended</p>
          {recommendations.map((rec, idx) => {
            const cost = getRecommendationCostEstimate(rec);
            return (
              <div
                key={idx}
                className="flex items-baseline justify-between gap-3 rounded-md bg-muted/20 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <p className="truncate text-foreground">{rec.line_item}</p>
                  {rec.basis && <p className="truncate text-xs text-muted-foreground">{rec.basis}</p>}
                </div>
                {cost != null && <span className="font-semibold text-foreground">{usd(cost)}</span>}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Is this right?</span>
        {(
          [
            { v: "agree", Icon: ThumbsUp, label: "Agree" },
            { v: "disagree", Icon: ThumbsDown, label: "Disagree" },
            { v: "unsure", Icon: HelpCircle, label: "Unsure" },
          ] as const
        ).map(({ v, Icon, label }) => (
          <button
            key={v}
            type="button"
            disabled={submitting !== null || !deficiency.id}
            onClick={() => sendFeedback(v)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ring-1 ring-border/40 transition-colors hover:bg-muted/40",
              submitted === v && "bg-primary/15 text-primary ring-primary/30",
            )}
          >
            <Icon className="h-3 w-3" />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
